import type { paths } from "@src/packages/openapi/schema";

type UpdatePhaseBody = NonNullable<
  paths["/{tenantId}/cycles/{id}/phases/{phaseId}"]["patch"]["requestBody"]
>["content"]["application/json"];

/** The phase's embryo rows as a write: upserts, and deletes by id. */
export type PhaseEmbryoDataWrite = NonNullable<UpdatePhaseBody["embryoData"]>;

/**
 * Embryo grades, one per embryo.
 *
 * The embryo-development step used to ask for one "top embryo grade" and kept
 * it as the phase's first `embryoData` row. A clinic grades each embryo, so the
 * step now keeps one row per embryo, and the best grade is only ever worked out
 * from them. `embryoData` stores a grade and a code for each embryo and no day
 * of development, so the step asks for no day.
 *
 * Step data is a flat record of scalars from the form all the way to the phase
 * write, so the list travels through it as one JSON field, encoded and decoded
 * only here.
 */
export const EMBRYO_GRADES_FIELD = "embryoGrades";

/** The single grade the step kept before it kept one per embryo. */
const LEGACY_GRADE_FIELD = "embryoGrade";

export type EmbryoGradeEntry = {
  /** The stored row, or null for an embryo added on this form. */
  id: string | null;
  grade: string | null;
};

type StoredEmbryo = {
  id: string;
  grade: string | null;
  createdAt?: string | null;
};

/**
 * The phase's embryos in the order they were added, so "Embryo 2" stays the
 * same embryo however the API happens to list them.
 */
export function embryoGradesFromPhase(
  embryoData: readonly StoredEmbryo[] | null | undefined,
): EmbryoGradeEntry[] {
  return [...(embryoData ?? [])]
    .sort((a, b) => (a.createdAt ?? "").localeCompare(b.createdAt ?? ""))
    .map(({ id, grade }) => ({ id, grade: grade || null }));
}

export function encodeEmbryoGrades(
  entries: readonly EmbryoGradeEntry[],
): string {
  return JSON.stringify(entries.map(({ id, grade }) => ({ id, grade })));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** The list a step field holds, or null when it holds none. */
export function decodeEmbryoGrades(value: unknown): EmbryoGradeEntry[] | null {
  if (typeof value !== "string" || value.length === 0) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter(isRecord).map((item) => ({
      id: typeof item.id === "string" && item.id ? item.id : null,
      grade: typeof item.grade === "string" && item.grade ? item.grade : null,
    }));
  } catch {
    return null;
  }
}

/**
 * The embryos a step's data holds: its list, or, for data saved before there
 * was one, the single grade as the first embryo.
 */
export function readEmbryoGrades(
  data: Readonly<Record<string, unknown>>,
): EmbryoGradeEntry[] {
  const list = decodeEmbryoGrades(data[EMBRYO_GRADES_FIELD]);
  if (list) return list;
  const single = data[LEGACY_GRADE_FIELD];
  return typeof single === "string" && single
    ? [{ id: null, grade: single }]
    : [];
}

/** The graded embryos' grades, in embryo order. */
export function gradedEmbryoGrades(
  entries: readonly EmbryoGradeEntry[],
): string[] {
  return entries.flatMap(({ grade }) => (grade ? [grade] : []));
}

/**
 * The best grade among the embryos, and which embryo has it.
 *
 * Ranked by the step's own list of grades, best first. A grade that list does
 * not know ranks below every grade it does, and a tie goes to the earlier
 * embryo. A summary only: nothing stores it.
 */
export function bestEmbryoGrade(
  entries: readonly EmbryoGradeEntry[],
  gradesBestFirst: readonly string[],
): { grade: string; embryoNumber: number } | null {
  let best: { grade: string; embryoNumber: number } | null = null;
  let bestRank = Number.POSITIVE_INFINITY;
  for (const [index, { grade }] of entries.entries()) {
    if (!grade) continue;
    const known = gradesBestFirst.indexOf(grade);
    const rank = known === -1 ? Number.POSITIVE_INFINITY : known;
    if (!best || rank < bestRank) {
      best = { grade, embryoNumber: index + 1 };
      bestRank = rank;
    }
  }
  return best;
}

/**
 * The write that makes the phase hold exactly these embryos.
 *
 * A graded embryo is upserted, by id when it is already stored. An embryo with
 * no grade is not one she has said anything about, so it is not written; a
 * stored one she has taken the grade off is deleted, which is the only way an
 * embryo leaves the list.
 */
export function buildEmbryoDataWrite(
  entries: readonly EmbryoGradeEntry[],
  stored: readonly { id: string }[],
): PhaseEmbryoDataWrite {
  const upserts: PhaseEmbryoDataWrite = entries.flatMap(({ id, grade }) =>
    grade ? [id ? { id, grade } : { grade }] : [],
  );
  const kept = new Set(
    entries.flatMap(({ id, grade }) => (id && grade ? [id] : [])),
  );
  const deletes: PhaseEmbryoDataWrite = stored
    .filter(({ id }) => !kept.has(id))
    .map(({ id }) => ({ id, op: "delete" as const }));
  return [...upserts, ...deletes];
}
